import React from "react";
import {
    Alert,
    Card,
    Col,
    Form,
    InputGroup,
    Row,
} from "react-bootstrap";
import { useFormikContext } from "formik";

const FormSection = ({ title, level = 1, children, button = null }) => {
    const Tag = `h${Math.min(6, level + 3)}`;

    return (
        <section className="mb-4 form-section">
            <Tag
                className={`text-muted ${level === 1 ? "fw-semibold" : "fw-medium"
                    }`}
            >
                {title} {button}
            </Tag>
            {children}
        </section>
    );
};

const integerOrEmpty = (raw) => {
    if (raw === "") return "";

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : "";
};

const decimalOrEmpty = (raw) => {
    if (raw === "") return "";

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : "";
};

export default function MileageTab() {
    const {
        values,
        setFieldValue,
        submitCount,
    } = useFormikContext();

    const rentalPolicy = values.rentalPolicy || {};
    const mileageServices = values.mileageServices || {};

    const isUnlimited = Boolean(rentalPolicy.isUnlimitedMileage);
    const hasLimit = !isUnlimited;
    const overageEnabled =
        hasLimit && Boolean(values.ui?.overageEnabled);

    const freeUnlimited =
        mileageServices.freeUnlimitedAfterDays || {};
    const paidUnlimited =
        mileageServices.unlimitedPerDay || {};
    const limitedPackage =
        mileageServices.limitedKmPerDay || {};

    const setServiceEnabled = (serviceKey, enabled) => {
        setFieldValue(
            `mileageServices.${serviceKey}.enabled`,
            enabled
        );

        if (enabled) return;

        if (serviceKey === "freeUnlimitedAfterDays") {
            setFieldValue(
                "mileageServices.freeUnlimitedAfterDays.minRentalDays",
                ""
            );
        }

        if (serviceKey === "unlimitedPerDay") {
            setFieldValue(
                "mileageServices.unlimitedPerDay.pricePerDay",
                ""
            );
            setFieldValue(
                "mileageServices.unlimitedPerDay.isRequired",
                false
            );
        }

        if (serviceKey === "limitedKmPerDay") {
            setFieldValue(
                "mileageServices.limitedKmPerDay.pricePerDay",
                ""
            );
            setFieldValue(
                "mileageServices.limitedKmPerDay.kilometersPerDay",
                ""
            );
        }
    };

    const freeUnlimitedIncomplete =
        Boolean(freeUnlimited.enabled) &&
        !(Number(freeUnlimited.minRentalDays) > 0);

    const paidUnlimitedIncomplete =
        Boolean(paidUnlimited.enabled) &&
        !(Number(paidUnlimited.pricePerDay) > 0);

    const limitedPackageIncomplete =
        Boolean(limitedPackage.enabled) &&
        !(
            Number(limitedPackage.pricePerDay) > 0 &&
            Number(limitedPackage.kilometersPerDay) > 0
        );

    return (
        <>
            <FormSection title="Included Mileage" level={2}>
                <Row className="g-3">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Mileage Policy</Form.Label>
                            <Form.Select
                                value={
                                    hasLimit
                                        ? "LIMITED"
                                        : "UNLIMITED"
                                }
                                onChange={(event) => {
                                    const nextHasLimit =
                                        event.target.value === "LIMITED";

                                    setFieldValue(
                                        "rentalPolicy.isUnlimitedMileage",
                                        !nextHasLimit
                                    );

                                    if (!nextHasLimit) {
                                        setFieldValue(
                                            "rentalPolicy.mileageLimit",
                                            null
                                        );
                                        setFieldValue(
                                            "rentalPolicy.excessMileageFee",
                                            0
                                        );
                                        setFieldValue(
                                            "ui.overageEnabled",
                                            false
                                        );
                                    }
                                }}
                            >
                                <option value="UNLIMITED">
                                    Unlimited mileage
                                </option>
                                <option value="LIMITED">
                                    Limited mileage
                                </option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Define the mileage included in the
                                standard rental.
                            </Form.Text>
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>
                                Included Mileage per Day
                                {hasLimit && (
                                    <span className="text-danger">
                                        {" "}*
                                    </span>
                                )}
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={
                                        rentalPolicy.mileageLimit ?? ""
                                    }
                                    disabled={!hasLimit}
                                    onChange={(event) =>
                                        setFieldValue(
                                            "rentalPolicy.mileageLimit",
                                            integerOrEmpty(
                                                event.target.value
                                            )
                                        )
                                    }
                                    placeholder="e.g. 100"
                                />
                                <InputGroup.Text>
                                    km/day
                                </InputGroup.Text>
                            </InputGroup>
                        </Form.Group>
                    </Col>

                    {hasLimit && (
                        <>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Check
                                        type="switch"
                                        id="ui.overageEnabled"
                                        label="Allow Paid Overage"
                                        checked={overageEnabled}
                                        onChange={(event) => {
                                            const enabled =
                                                event.target.checked;

                                            setFieldValue(
                                                "ui.overageEnabled",
                                                enabled
                                            );

                                            if (!enabled) {
                                                setFieldValue(
                                                    "rentalPolicy.excessMileageFee",
                                                    0
                                                );
                                            } else if (
                                                !(
                                                    Number(
                                                        rentalPolicy.excessMileageFee ??
                                                        0
                                                    ) > 0
                                                )
                                            ) {
                                                setFieldValue(
                                                    "rentalPolicy.excessMileageFee",
                                                    0.05
                                                );
                                            }
                                        }}
                                    />
                                    <Form.Text className="text-muted">
                                        Charge customers when they
                                        exceed the included mileage.
                                    </Form.Text>
                                </Form.Group>
                            </Col>

                            {overageEnabled && (
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Overage Price{" "}
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                inputMode="decimal"
                                                min={0.01}
                                                step="0.01"
                                                value={
                                                    rentalPolicy.excessMileageFee ??
                                                    ""
                                                }
                                                onChange={(event) =>
                                                    setFieldValue(
                                                        "rentalPolicy.excessMileageFee",
                                                        decimalOrEmpty(
                                                            event.target.value
                                                        )
                                                    )
                                                }
                                                placeholder="e.g. 0.25"
                                            />
                                            <InputGroup.Text>
                                                €/km
                                            </InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            )}
                        </>
                    )}
                </Row>
            </FormSection>

            <FormSection title="Mileage Packages" level={2}>
                {isUnlimited && (
                    <Alert variant="info" className="py-2 mb-4">
                        The standard rental already includes unlimited
                        mileage. Additional mileage packages are
                        usually not needed.
                    </Alert>
                )}

                <Card className="mb-4">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-start gap-3">
                            <div>
                                <div className="fw-semibold">
                                    Free Unlimited Mileage
                                </div>
                                <Form.Text className="text-muted">
                                    Automatically include unlimited
                                    mileage for longer rentals.
                                </Form.Text>
                            </div>

                            <Form.Check
                                type="switch"
                                id="mileageServices.freeUnlimitedAfterDays.enabled"
                                label={
                                    freeUnlimited.enabled
                                        ? "Enabled"
                                        : "Disabled"
                                }
                                checked={Boolean(
                                    freeUnlimited.enabled
                                )}
                                onChange={(event) =>
                                    setServiceEnabled(
                                        "freeUnlimitedAfterDays",
                                        event.target.checked
                                    )
                                }
                            />
                        </div>
                    </Card.Header>

                    {freeUnlimited.enabled && (
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Minimum Rental Duration{" "}
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                min={1}
                                                step={1}
                                                value={
                                                    freeUnlimited.minRentalDays ??
                                                    ""
                                                }
                                                onChange={(event) =>
                                                    setFieldValue(
                                                        "mileageServices.freeUnlimitedAfterDays.minRentalDays",
                                                        integerOrEmpty(
                                                            event.target.value
                                                        )
                                                    )
                                                }
                                                placeholder="e.g. 7"
                                            />
                                            <InputGroup.Text>
                                                days
                                            </InputGroup.Text>
                                        </InputGroup>
                                        <Form.Text className="text-muted">
                                            Unlimited mileage starts
                                            when the rental reaches this
                                            number of days.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {submitCount > 0 &&
                                freeUnlimitedIncomplete && (
                                    <Alert
                                        variant="danger"
                                        className="py-2 mt-3 mb-0"
                                    >
                                        Minimum rental duration is
                                        required.
                                    </Alert>
                                )}
                        </Card.Body>
                    )}
                </Card>

                <Card className="mb-4">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-start gap-3">
                            <div>
                                <div className="fw-semibold">
                                    Unlimited Mileage Upgrade
                                </div>
                                <Form.Text className="text-muted">
                                    Offer unlimited mileage as a paid
                                    daily upgrade.
                                </Form.Text>
                            </div>

                            <Form.Check
                                type="switch"
                                id="mileageServices.unlimitedPerDay.enabled"
                                label={
                                    paidUnlimited.enabled
                                        ? "Enabled"
                                        : "Disabled"
                                }
                                checked={Boolean(
                                    paidUnlimited.enabled
                                )}
                                onChange={(event) =>
                                    setServiceEnabled(
                                        "unlimitedPerDay",
                                        event.target.checked
                                    )
                                }
                            />
                        </div>
                    </Card.Header>

                    {paidUnlimited.enabled && (
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Daily Price{" "}
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                inputMode="decimal"
                                                min={0.01}
                                                step="0.01"
                                                value={
                                                    paidUnlimited.pricePerDay ??
                                                    ""
                                                }
                                                onChange={(event) =>
                                                    setFieldValue(
                                                        "mileageServices.unlimitedPerDay.pricePerDay",
                                                        decimalOrEmpty(
                                                            event.target.value
                                                        )
                                                    )
                                                }
                                                placeholder="e.g. 15.00"
                                            />
                                            <InputGroup.Text>
                                                €/day
                                            </InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Rental Requirement
                                        </Form.Label>
                                        <div className="pt-2">
                                            <Form.Check
                                                type="switch"
                                                id="mileageServices.unlimitedPerDay.isRequired"
                                                label="Mandatory"
                                                checked={Boolean(
                                                    paidUnlimited.isRequired
                                                )}
                                                onChange={(event) =>
                                                    setFieldValue(
                                                        "mileageServices.unlimitedPerDay.isRequired",
                                                        event.target.checked
                                                    )
                                                }
                                            />
                                        </div>
                                        <Form.Text className="text-muted">
                                            Customers cannot remove
                                            this package during booking.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {submitCount > 0 &&
                                paidUnlimitedIncomplete && (
                                    <Alert
                                        variant="danger"
                                        className="py-2 mt-3 mb-0"
                                    >
                                        Daily price is required.
                                    </Alert>
                                )}
                        </Card.Body>
                    )}
                </Card>

                <Card className="mb-4">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-start gap-3">
                            <div>
                                <div className="fw-semibold">
                                    Extra Mileage Package
                                </div>
                                <Form.Text className="text-muted">
                                    Offer additional kilometers for a
                                    daily price.
                                </Form.Text>
                            </div>

                            <Form.Check
                                type="switch"
                                id="mileageServices.limitedKmPerDay.enabled"
                                label={
                                    limitedPackage.enabled
                                        ? "Enabled"
                                        : "Disabled"
                                }
                                checked={Boolean(
                                    limitedPackage.enabled
                                )}
                                onChange={(event) =>
                                    setServiceEnabled(
                                        "limitedKmPerDay",
                                        event.target.checked
                                    )
                                }
                            />
                        </div>
                    </Card.Header>

                    {limitedPackage.enabled && (
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Additional Mileage per Day{" "}
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                min={1}
                                                step={1}
                                                value={
                                                    limitedPackage.kilometersPerDay ??
                                                    ""
                                                }
                                                onChange={(event) =>
                                                    setFieldValue(
                                                        "mileageServices.limitedKmPerDay.kilometersPerDay",
                                                        integerOrEmpty(
                                                            event.target.value
                                                        )
                                                    )
                                                }
                                                placeholder="e.g. 30"
                                            />
                                            <InputGroup.Text>
                                                km/day
                                            </InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Daily Price{" "}
                                            <span className="text-danger">
                                                *
                                            </span>
                                        </Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                inputMode="decimal"
                                                min={0.01}
                                                step="0.01"
                                                value={
                                                    limitedPackage.pricePerDay ??
                                                    ""
                                                }
                                                onChange={(event) =>
                                                    setFieldValue(
                                                        "mileageServices.limitedKmPerDay.pricePerDay",
                                                        decimalOrEmpty(
                                                            event.target.value
                                                        )
                                                    )
                                                }
                                                placeholder="e.g. 8.00"
                                            />
                                            <InputGroup.Text>
                                                €/day
                                            </InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {submitCount > 0 &&
                                limitedPackageIncomplete && (
                                    <Alert
                                        variant="danger"
                                        className="py-2 mt-3 mb-0"
                                    >
                                        Additional mileage and daily
                                        price are required.
                                    </Alert>
                                )}
                        </Card.Body>
                    )}
                </Card>
            </FormSection>
        </>
    );
}
